import React, { useState } from 'react';
import Select from 'react-select';
import Modal from '../Modal';
import { getActionSelectStyles } from '../../utility/selectStyles';
import { DefaultKind, TypeToken, defaultOptions, nameProblem, nameProblemLine, toSnakeCase, typeOptions } from './tableName';

interface Busy { busy: string | null; line: string | null }

/** Add column (DESIGN_EDITING.md console flow 7): name, type, Required, Default. Returns the add_column change. */
export const AddColumnModal: React.FC<Busy & { expert: boolean; existing: string[]; onAdd: (change: any) => void; onClose: () => void }> = ({ expert, existing, busy, line, onAdd, onClose }) => {
    const [name, setName] = useState('');
    const [type, setType] = useState<TypeToken>('text');
    const [required, setRequired] = useState(false);
    const [defaultKind, setDefaultKind] = useState<DefaultKind>('none');
    const [defaultValue, setDefaultValue] = useState('');
    const running = busy !== null;
    const types = typeOptions(expert).map((o) => ({ value: o.token, label: o.label }));
    const defs = defaultOptions(type, { atCreate: false, isKey: false }).map((o) => ({ value: o.kind, label: o.label }));
    const p = nameProblem(name);
    const taken = !p && existing.includes(name);
    const valueMissing = defaultKind === 'value' && defaultValue === '';
    const problem = p ? nameProblemLine(p) : taken ? 'Name taken.' : valueMissing ? 'Enter a default value, or choose None.' : null;
    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        if (running || problem) return;
        onAdd({ kind: 'add_column', column: { name, type, ...(required ? { not_null: true } : {}), default: defaultKind === 'value' ? { kind: 'value', value: defaultValue } : { kind: defaultKind } } });
    };
    return (
        <Modal isOpen onClose={() => { if (!running) onClose(); }} title="Add column" size="sm" closeOnEscape={!running} showCloseButton={!running}>
            <form onSubmit={submit} className="db-data-form">
                <div className="form-group">
                    <label htmlFor="db-add-col-name">Name</label>
                    <input id="db-add-col-name" value={name} disabled={running} onChange={(e) => setName(e.target.value)} onBlur={() => setName((n) => toSnakeCase(n))} />
                </div>
                <div className="form-group">
                    <label>Type</label>
                    <Select aria-label="Type" options={types} value={types.find((o) => o.value === type)} isDisabled={running}
                        onChange={(o: any) => { const tt = (o?.value ?? 'text') as TypeToken; setType(tt); const allowed = defaultOptions(tt, { atCreate: false, isKey: false }).map((d) => d.kind); if (!allowed.includes(defaultKind)) setDefaultKind('none'); }}
                        styles={getActionSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed" />
                </div>
                <label className="db-data-col-flag"><input type="checkbox" checked={required} disabled={running} onChange={(e) => setRequired(e.target.checked)} /> Required</label>
                <div className="form-group">
                    <label>Default</label>
                    <Select aria-label="Default" options={defs} value={defs.find((o) => o.value === defaultKind) ?? defs[0]} isDisabled={running}
                        onChange={(o: any) => setDefaultKind((o?.value ?? 'none') as DefaultKind)} styles={getActionSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed" />
                </div>
                {defaultKind === 'value' && (
                    <div className="form-group"><input aria-label="Default value" value={defaultValue} placeholder="value" disabled={running} onChange={(e) => setDefaultValue(e.target.value)} /></div>
                )}
                {problem && !running && <p className="db-data-field-line">{problem}</p>}
                {line && <p className="db-data-write-line" role="alert"><span>{line}</span></p>}
                <div className="modal-actions">
                    <button type="button" className="cancel-button" onClick={onClose} disabled={running}>Cancel</button>
                    <button type="submit" className="submit-button" disabled={running || !!problem}>{busy ?? 'Add'}</button>
                </div>
            </form>
        </Modal>
    );
};

/** Rename a table or a column: one field. `change` is built by the caller from the new name. */
export const RenameModal: React.FC<Busy & { title: string; current: string; existing: string[]; onRename: (to: string) => void; onClose: () => void }> = ({ title, current, existing, busy, line, onRename, onClose }) => {
    const [to, setTo] = useState('');
    const running = busy !== null;
    const p = nameProblem(to);
    const taken = !p && to !== current && existing.includes(to);
    const problem = p ? nameProblemLine(p) : taken ? 'Name taken.' : null;
    const submit = (e: React.FormEvent) => { e.preventDefault(); if (running || problem) return; onRename(to); };
    return (
        <Modal isOpen onClose={() => { if (!running) onClose(); }} title={title} size="sm" closeOnEscape={!running} showCloseButton={!running}>
            <form onSubmit={submit} className="db-data-form">
                <div className="form-group">
                    <label htmlFor="db-rename-to">New name</label>
                    <input id="db-rename-to" value={to} placeholder={current} disabled={running} autoFocus onChange={(e) => setTo(e.target.value)} onBlur={() => setTo((n) => toSnakeCase(n))} />
                </div>
                {problem && !running && <p className="db-data-field-line">{problem}</p>}
                {line && <p className="db-data-write-line" role="alert"><span>{line}</span></p>}
                <div className="modal-actions">
                    <button type="button" className="cancel-button" onClick={onClose} disabled={running}>Cancel</button>
                    <button type="submit" className="submit-button" disabled={running || !!problem}>{busy ?? 'Rename'}</button>
                </div>
            </form>
        </Modal>
    );
};

/** Delete a column (flow 7): the name must be typed to match, and it cannot be undone. */
export const DeleteColumnModal: React.FC<Busy & { column: string; onDelete: () => void; onClose: () => void }> = ({ column, busy, line, onDelete, onClose }) => {
    const [typed, setTyped] = useState('');
    const running = busy !== null;
    const submit = (e: React.FormEvent) => { e.preventDefault(); if (running || typed !== column) return; onDelete(); };
    return (
        <Modal isOpen onClose={() => { if (!running) onClose(); }} title={`Delete column ${column}`} size="sm" closeOnEscape={!running} showCloseButton={!running}>
            <form onSubmit={submit} className="db-data-form">
                <p className="db-data-field-line">This cannot be undone.</p>
                <div className="form-group">
                    <input aria-label="Type the column name to confirm" value={typed} placeholder={column} disabled={running} autoFocus onChange={(e) => setTyped(e.target.value)} />
                </div>
                {line && <p className="db-data-write-line" role="alert"><span>{line}</span></p>}
                <div className="modal-actions">
                    <button type="button" className="cancel-button" onClick={onClose} disabled={running}>Cancel</button>
                    <button type="submit" className="db-data-delete-button" disabled={running || typed !== column}>{busy ?? 'Delete'}</button>
                </div>
            </form>
        </Modal>
    );
};
